import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth-context';
import { GuestAuthProvider } from './lib/guest-auth-context';

// Layout
import AdminLayout from './components/layout/AdminLayout';
import GuestLayout from './components/layout/GuestLayout';

// Pages
import LoginPage from './components/pages/LoginPage';
import DashboardPage from './components/pages/DashboardPage';
import PropertiesPage from './components/pages/PropertiesPage';
import UsersPage from './components/pages/UsersPage';
import RolesPage from './components/pages/RolesPage';
import AuditPage from './components/pages/AuditPage';
import RoomsPage from './components/pages/RoomsPage';
// Phase 2: Reservations Module
import ReservationsPage from './components/pages/ReservationsPage';
import AvailabilityPage from './components/pages/AvailabilityPage';
import GuestsPage from './components/pages/GuestsPage';
import RatesPage from './components/pages/RatesPage';
// Phase 3: Front Office Module
import FrontDeskPage from './components/pages/FrontDeskPage';
import FoliosPage from './components/pages/FoliosPage';
import RoomStatusPage from './components/pages/RoomStatusPage';
// Phase 4: Housekeeping & Finance Module
import HousekeepingPage from './components/pages/HousekeepingPage';
import FinancePage from './components/pages/FinancePage';
import NightAuditPage from './components/pages/NightAuditPage';
import FnbCostControlPage from './components/pages/FnbCostControlPage';
import VenueBookingPage from './components/pages/VenueBookingPage';
import AccountingPage from './components/pages/AccountingPage';
import CreditDepositsPage from './components/pages/CreditDepositsPage';
import StoresExpiryPage from './components/pages/StoresExpiryPage';
import OutsourcingPage from './components/pages/OutsourcingPage';
import SurveysPage from './components/pages/SurveysPage';
// Phase 5: POS & Maintenance Module
import POSPage from './components/pages/POSPage';
import MaintenancePage from './components/pages/MaintenancePage';
// Phase 6: Guest Portal Module
import GuestPortalPage from './components/pages/GuestPortalPage';
import GuestLoginPage from './components/pages/GuestLoginPage';
import GuestRegisterPage from './components/pages/GuestRegisterPage';
import GuestDashboardPage from './components/pages/GuestDashboardPage';
import GuestBookingPage from './components/pages/GuestBookingPage';
import BookingConfirmationPage from './components/pages/BookingConfirmationPage';
import NewServiceRequestPage from './components/pages/NewServiceRequestPage';
import PreCheckinPage from './components/pages/PreCheckinPage';

// Phase 7: Distribution, CRM/Loyalty, Spa
import DistributionPage from './components/pages/DistributionPage';
import CRMLoyaltyPage from './components/pages/CRMLoyaltyPage';
import SpaPage from './components/pages/SpaPage';

// Phase 8: Events/MICE
import EventsPage from './components/pages/EventsPage';

// Phase 9: Procurement & Inventory
import ProcurementPage from './components/pages/ProcurementPage';

// Phase 21: Inventory Management (Enhanced)
import InventoryPage from './components/pages/InventoryPage';

// Phase 10: Analytics & Revenue
import AnalyticsPage from './components/pages/AnalyticsPage';

// Phase 11: Reports
import ReportsPage from './components/pages/ReportsPage';

// Phase 12: Workforce Management
import WorkforcePage from './components/pages/WorkforcePage';

// Phase 13: Concierge & Transportation
import ConciergePage from './components/pages/ConciergePage';

// Phase 14: Smart Room & IoT
import SmartRoomPage from './components/pages/SmartRoomPage';

// Phase 15: Integrations & Marketplace
import IntegrationsPage from './components/pages/IntegrationsPage';

// Phase 16: Energy & Sustainability
import EnergyPage from './components/pages/EnergyPage';

// Phase 17: Localization
import LocalizationPage from './components/pages/LocalizationPage';

// Phase 18: Portfolio Management
import PortfolioPage from './components/pages/PortfolioPage';

// Phase 19: Disaster Recovery
import DisasterRecoveryPage from './components/pages/DisasterRecoveryPage';

// Phase 20: Hotel Registration
import AdminRegisterPage from './components/pages/AdminRegisterPage';
import RegistrationApprovalsPage from './components/pages/RegistrationApprovalsPage';

// Guest Portal Pages
import GuestSpaPage from './components/pages/GuestSpaPage';
import GuestEventsPage from './components/pages/GuestEventsPage';
import GuestProfilePage from './components/pages/GuestProfilePage';
import GuestSmartRoomPage from './components/pages/GuestSmartRoomPage';
import GuestLoyaltyPage from './components/pages/GuestLoyaltyPage';
import GuestReservationDetailPage from './components/pages/GuestReservationDetailPage';
import GuestFolioPage from './components/pages/GuestFolioPage';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Public Route wrapper (redirects if authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          
          {/* Hotel Registration - Public */}
          <Route
            path="/register"
            element={
              <PublicRoute>
                <AdminRegisterPage />
              </PublicRoute>
            }
          />

          {/* Protected admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="front-desk" element={<FrontDeskPage />} />
            <Route path="reservations" element={<ReservationsPage />} />
            <Route path="availability" element={<AvailabilityPage />} />
            <Route path="guests" element={<GuestsPage />} />
            <Route path="rates" element={<RatesPage />} />
            <Route path="folios" element={<FoliosPage />} />
            <Route path="room-status" element={<RoomStatusPage />} />
            <Route path="housekeeping" element={<HousekeepingPage />} />
            <Route path="pos" element={<POSPage />} />
            <Route path="maintenance" element={<MaintenancePage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="night-audit" element={<NightAuditPage />} />
            <Route path="fnb-cost-control" element={<FnbCostControlPage />} />
            <Route path="venue-bookings" element={<VenueBookingPage />} />
            <Route path="accounting" element={<AccountingPage />} />
            <Route path="credit" element={<CreditDepositsPage />} />
            <Route path="stores" element={<StoresExpiryPage />} />
            <Route path="outsourcing" element={<OutsourcingPage />} />
            <Route path="surveys" element={<SurveysPage />} />
            <Route path="properties" element={<PropertiesPage />} />
            <Route path="rooms" element={<RoomsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="audit" element={<AuditPage />} />
            {/* Phase 7: Distribution, CRM, Spa */}
            <Route path="distribution" element={<DistributionPage />} />
            <Route path="crm" element={<CRMLoyaltyPage />} />
            <Route path="spa" element={<SpaPage />} />
            {/* Phase 8: Events/MICE */}
            <Route path="events" element={<EventsPage />} />
            {/* Phase 9: Procurement & Inventory */}
            <Route path="procurement" element={<ProcurementPage />} />
            {/* Phase 21: Inventory Management */}
            <Route path="inventory" element={<InventoryPage />} />
            {/* Phase 10: Analytics & Revenue */}
            <Route path="analytics" element={<AnalyticsPage />} />
            {/* Phase 11: Reports */}
            <Route path="reports" element={<ReportsPage />} />
            {/* Phase 12: Workforce Management */}
            <Route path="workforce" element={<WorkforcePage />} />
            {/* Phase 13: Concierge & Transportation */}
            <Route path="concierge" element={<ConciergePage />} />
            {/* Phase 14: Smart Room & IoT */}
            <Route path="smart-room" element={<SmartRoomPage />} />
            {/* Phase 15: Integrations & Marketplace */}
            <Route path="integrations" element={<IntegrationsPage />} />
            {/* Phase 16: Energy & Sustainability */}
            <Route path="energy" element={<EnergyPage />} />
            {/* Phase 17: Localization */}
            <Route path="localization" element={<LocalizationPage />} />
            {/* Phase 18: Portfolio Management */}
            <Route path="portfolio" element={<PortfolioPage />} />
            {/* Phase 19: Disaster Recovery */}
            <Route path="disaster-recovery" element={<DisasterRecoveryPage />} />
            {/* Phase 20: Registration Approvals */}
            <Route path="registrations" element={<RegistrationApprovalsPage />} />
          </Route>

          {/* Guest Portal routes with GuestLayout */}
          <Route path="/portal" element={<GuestAuthProvider><GuestLayout /></GuestAuthProvider>}>
            <Route index element={<GuestPortalPage />} />
            <Route path="login" element={<GuestLoginPage />} />
            <Route path="register" element={<GuestRegisterPage />} />
            <Route path="dashboard" element={<GuestDashboardPage />} />
            <Route path="booking" element={<GuestBookingPage />} />
            <Route path="booking/confirmation" element={<BookingConfirmationPage />} />
            <Route path="requests/new" element={<NewServiceRequestPage />} />
            <Route path="reservations/:reservationId/pre-checkin" element={<PreCheckinPage />} />
            <Route path="reservations/:reservationId" element={<GuestReservationDetailPage />} />
            <Route path="reservations/:reservationId/folio" element={<GuestFolioPage />} />
            <Route path="spa" element={<GuestSpaPage />} />
            <Route path="events" element={<GuestEventsPage />} />
            <Route path="profile" element={<GuestProfilePage />} />
            <Route path="smart-room" element={<GuestSmartRoomPage />} />
            <Route path="loyalty" element={<GuestLoyaltyPage />} />
          </Route>

          {/* Redirect root to portal */}
          <Route path="/" element={<Navigate to="/portal" replace />} />

          {/* Catch all - redirect to portal */}
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
